import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { 
  LayoutDashboard, 
  Calendar, 
  Coffee,
  Users,
  Briefcase,
  BarChart2,
  Settings,
  LogOut,
  User
} from 'lucide-react'

interface DashboardLayoutProps {
  children: React.ReactNode
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  useEffect(() => {
    // Lade User-Info aus localStorage
    const userStr = localStorage.getItem('dashboard_user')
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr))
      } catch (e) {
        console.error('Error parsing user data:', e)
      }
    }
  }, [])

  const menuCategories = [
    {
      title: 'ÜBERSICHT',
      items: [
        { icon: LayoutDashboard, label: 'Übersicht', path: '/dashboard' },
      ]
    },
    {
      title: 'INHALTE',
      items: [
        { icon: Calendar, label: 'Events', path: '/dashboard/events' },
        { icon: Coffee, label: 'Brunch', path: '/dashboard/brunches' },
      ]
    },
    {
      title: 'KOMMUNIKATION',
      items: [
        { icon: Briefcase, label: 'Bewerbung', path: '/dashboard/bewerbung' },
      ]
    },
    {
      title: 'STATISTIK',
      items: [
        { icon: BarChart2, label: 'Statistiken', path: '/dashboard/statistik' },
        { icon: BarChart2, label: 'Events & Partys', path: '/dashboard/statistik/partys' },
        { icon: BarChart2, label: 'Brunch', path: '/dashboard/statistik/brunch' },
      ]
    },
    {
      title: 'SYSTEM',
      items: [
        { icon: Users, label: 'Benutzer', path: '/dashboard/users' },
        { icon: Settings, label: 'Einstellungen', path: '/dashboard/settings' },
      ]
    },
  ]

  const handleLogout = () => {
    localStorage.removeItem('dashboard_token')
    navigate('/dashboard/login')
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-gray-900 via-gray-900 to-gray-900 relative">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-500/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Mobile Menu Button - safe area for notches */}
      <motion.button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden bg-zinc-950/95 backdrop-blur-xl border border-white/10 rounded-xl p-3 shadow-xl min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
        aria-label="Menü öffnen"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          {isSidebarOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </motion.button>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
        />
      )}

      {/* Sidebar - kompakt, ohne Scroll, alles sichtbar */}
      <aside className="fixed left-0 top-0 h-full w-72 bg-zinc-950/95 backdrop-blur-2xl border-r border-white/10 shadow-2xl z-40 hidden lg:flex lg:flex-col lg:overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-white/[0.02] pointer-events-none" />
        
        {/* Logo & Title – kompakt */}
        <div className="relative flex-shrink-0 p-3 sm:p-4 border-b border-white/10">
          <Link to="/" className="block">
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-2 mb-2 group"
              whileHover={{ x: 2 }}
            >
              <img 
                src="/Logo_HM.png" 
                alt="Hückels May" 
                className="h-9 w-auto drop-shadow-lg group-hover:drop-shadow-2xl transition-all"
              />
            </motion.div>
          </Link>
          <h2 className="font-serif text-lg font-light text-white">
            Dashboard
          </h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Verwaltungsbereich</p>
          <div className="h-px w-12 bg-gradient-to-r from-brand-500 to-brand-500 mt-2" />
          <Link 
            to="/"
            className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-300 hover:text-brand-400 transition-colors group"
          >
            <svg className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Zur Website
          </Link>
        </div>
        
        <nav className="relative flex-1 min-h-0 flex flex-col py-2 px-3 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden space-y-2">
            {menuCategories.map((category, categoryIdx) => (
              <div key={category.title} className="space-y-0.5">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: categoryIdx * 0.1 }}
                  className="px-2.5 py-0.5"
                >
                  <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    {category.title}
                  </h3>
                </motion.div>
              
              {category.items.map((item, itemIdx) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path
                const globalIdx = categoryIdx * 10 + itemIdx
                return (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: globalIdx * 0.05 }}
                    whileHover={{ x: 4 }}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 group relative overflow-hidden ${
                        isActive
                          ? 'bg-gradient-to-r from-brand-600/20 to-brand-600/20 text-brand-400 border border-brand-500/30 shadow-lg shadow-brand-500/10'
                          : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/5 hover:border-white/10'
                      }`}
                    >
                      {/* Active indicator */}
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute inset-0 bg-gradient-to-r from-brand-600/10 to-brand-600/10"
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                      
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-gradient-to-b from-brand-500 to-brand-500 rounded-r-full" />
                      )}
                      <motion.div
                        whileHover={{ rotate: isActive ? 0 : 8, scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Icon className={`h-4 w-4 relative z-10 shrink-0 ${isActive ? 'text-brand-400' : 'text-gray-300 group-hover:text-white'}`} />
                      </motion.div>
                      <span className={`relative z-10 font-sans text-xs font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {item.label}
                      </span>
                      <svg 
                        className={`h-3.5 w-3.5 ml-auto relative z-10 shrink-0 transition-all duration-300 ${
                          isActive ? 'opacity-100 text-brand-400' : 'opacity-0 group-hover:opacity-100 text-white/50'
                        }`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </motion.div>
                )
              })}
              </div>
            ))}
          </div>
        </nav>

        {/* User + Logout fest unten, kein Überlagern */}
        {currentUser && (
          <div className="flex-shrink-0 p-2 pt-0 border-t border-white/10">
            <div className="bg-gradient-to-br from-brand-600/10 to-brand-600/10 border border-brand-500/20 rounded-lg p-2.5 backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-br from-brand-500 to-brand-500 p-1.5 rounded-md shrink-0">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white truncate">{currentUser.name || 'Admin'}</p>
                  <p className="text-[10px] text-gray-400 truncate">{currentUser.email}</p>
                </div>
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-emerald-500/50 animate-pulse shrink-0" title="Online" />
              </div>
            </div>
          </div>
        )}

        <div className="flex-shrink-0 p-2 border-t border-white/10 bg-gradient-to-t from-zinc-950/50 to-transparent">
          <motion.button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-red-500/10 hover:border-red-500/30 w-full transition-all duration-300 border border-white/5 hover:border-red-500/30 group"
            whileHover={{ x: 2, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <LogOut className="h-4 w-4 text-red-400/70 group-hover:text-red-400 shrink-0" />
            <span className="font-sans text-xs font-medium">Abmelden</span>
            <svg 
              className="h-3.5 w-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-red-400"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.button>
        </div>
      </aside>

      {/* Mobile Sidebar – gleiche kompakte Struktur wie Desktop */}
      {isSidebarOpen && (
        <motion.aside
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          exit={{ x: '-100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed left-0 top-0 h-full w-72 bg-zinc-950/95 backdrop-blur-2xl border-r border-white/10 shadow-2xl z-40 lg:hidden flex flex-col overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-white/[0.02] pointer-events-none" />
          
          <div className="relative flex-shrink-0 p-3 sm:p-4 border-b border-white/10">
            <Link to="/" className="block" onClick={() => setIsSidebarOpen(false)}>
              <motion.div className="flex items-center gap-2 mb-2 group" whileHover={{ x: 2 }}>
                <img src="/Logo_HM.png" alt="Hückels May" className="h-9 w-auto drop-shadow-lg transition-all" />
              </motion.div>
            </Link>
            <h2 className="font-serif text-lg font-light text-white">Dashboard</h2>
            <p className="text-[10px] text-gray-400 mt-0.5">Verwaltungsbereich</p>
            <div className="h-px w-12 bg-gradient-to-r from-brand-500 to-brand-500 mt-2" />
            <Link to="/" onClick={() => setIsSidebarOpen(false)} className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-300 hover:text-brand-400 transition-colors group">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              Zur Website
            </Link>
          </div>
          
          <nav className="relative flex-1 min-h-0 py-2 px-3 overflow-y-auto overflow-x-hidden">
            <div className="space-y-2 pb-2">
              {menuCategories.map((category, categoryIdx) => (
                <div key={category.title} className="space-y-0.5">
                  <div className="px-2.5 py-0.5">
                    <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{category.title}</h3>
                  </div>
                  {category.items.map((item) => {
                    const Icon = item.icon
                    const isActive = location.pathname === item.path
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsSidebarOpen(false)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 group relative overflow-hidden ${
                          isActive ? 'bg-gradient-to-r from-brand-600/20 to-brand-600/20 text-brand-400 border border-brand-500/30' : 'text-gray-300 hover:text-white hover:bg-white/5 border border-white/5 hover:border-white/10'
                        }`}
                      >
                        {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-gradient-to-b from-brand-500 to-brand-500 rounded-r-full" />}
                        <Icon className={`h-4 w-4 relative z-10 shrink-0 ${isActive ? 'text-brand-400' : 'text-gray-300 group-hover:text-white'}`} />
                        <span className={`relative z-10 font-sans text-xs font-medium truncate ${isActive ? 'text-white' : ''}`}>{item.label}</span>
                        <svg className={`h-3.5 w-3.5 ml-auto relative z-10 shrink-0 ${isActive ? 'opacity-100 text-brand-400' : 'opacity-0 group-hover:opacity-100 text-white/50'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </Link>
                    )
                  })}
                </div>
              ))}
            </div>
          </nav>

          {currentUser && (
            <div className="flex-shrink-0 p-2 pt-0 border-t border-white/10">
              <div className="bg-gradient-to-br from-brand-600/10 to-brand-600/10 border border-brand-500/20 rounded-lg p-2.5 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <div className="bg-gradient-to-br from-brand-500 to-brand-500 p-1.5 rounded-md shrink-0"><User className="h-4 w-4 text-white" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{currentUser.name || 'Admin'}</p>
                    <p className="text-[10px] text-gray-400 truncate">{currentUser.email}</p>
                  </div>
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shrink-0" title="Online" />
                </div>
              </div>
            </div>
          )}

          <div className="flex-shrink-0 p-2 border-t border-white/10 bg-gradient-to-t from-zinc-950/50 to-transparent">
            <motion.button
              onClick={() => { setIsSidebarOpen(false); handleLogout(); }}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-red-500/10 hover:border-red-500/30 w-full transition-all duration-300 border border-white/5 hover:border-red-500/30 group"
              whileTap={{ scale: 0.98 }}
            >
              <LogOut className="h-4 w-4 text-red-400/70 group-hover:text-red-400 shrink-0" />
              <span className="font-sans text-xs font-medium">Abmelden</span>
            </motion.button>
          </div>
        </motion.aside>
      )}

      {/* Main Content – Inhalt vollständig sichtbar, horizontal scrollbar wenn nötig */}
      <main className="lg:ml-72 w-full min-w-0 min-h-screen min-h-[100dvh] relative z-10 pt-20 lg:pt-8 pb-8 px-4 sm:px-6 lg:px-8 lg:max-w-[1920px] overflow-x-auto overflow-y-visible">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="min-w-full"
        >
          {children}
        </motion.div>
      </main>
    </div>
  )
}















